/**
 * Async Validation Service
 * Xử lý validation từ server với debounce và caching
 */

import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, first } from 'rxjs/operators';

export interface AsyncValidationConfig {
    /** API endpoint hoặc function gọi API */
    checkFn: (value: any) => Observable<boolean>;

    /** Error key khi validation fail */
    errorKey: string;

    /** Custom error message */
    errorMessage?: string;

    /** Debounce time (ms) */
    debounceTime?: number;

    /** Min length trước khi validate */
    minLength?: number;
}

@Injectable({
    providedIn: 'root'
})
export class AsyncValidationService {

    // Cache để tránh gọi API lặp lại
    private cache: Map<string, Map<string, boolean>> = new Map();

    /**
     * Tạo async validator từ config
     */
    createAsyncValidator(config: AsyncValidationConfig): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            const value = control.value;

            // Bỏ qua nếu value rỗng
            if (!value || value.trim() === '') {
                return of(null);
            }

            // Bỏ qua nếu value quá ngắn
            if (config.minLength && value.length < config.minLength) {
                return of(null);
            }

            // Check cache
            const cacheKey = `${config.errorKey}:${value}`;
            const cachedResult = this.getCachedResult(config.errorKey, value);
            if (cachedResult !== null) {
                return of(cachedResult ? { [config.errorKey]: true } : null);
            }

            // Debounce và gọi API
            const debounceMs = config.debounceTime || 300;

            return timer(debounceMs).pipe(
                switchMap(() => config.checkFn(value)),
                map(isTaken => {
                    // Cache kết quả
                    this.setCachedResult(config.errorKey, value, isTaken);

                    if (isTaken) {
                        return {
                            [config.errorKey]: {
                                value: true,
                                message: config.errorMessage || 'Giá trị đã tồn tại'
                            }
                        };
                    }
                    return null;
                }),
                catchError(() => {
                    // Trả về error nếu API fail
                    return of({
                        [config.errorKey + 'Error']: {
                            value: true,
                            message: 'Lỗi khi kiểm tra'
                        }
                    });
                }),
                first()
            );
        };
    }

    /**
     * Tạo validator kiểm tra code/mã đã tồn tại
     */
    createCodeExistsValidator(
        checkFn: (code: string) => Observable<boolean>,
        errorMessage: string = 'Mã đã tồn tại trên hệ thống'
    ): AsyncValidatorFn {
        return this.createAsyncValidator({
            checkFn,
            errorKey: 'codeTaken',
            errorMessage,
            debounceTime: 300,
            minLength: 1
        });
    }

    /**
     * Tạo validator kiểm tra email đã tồn tại
     */
    createEmailExistsValidator(
        checkFn: (email: string) => Observable<boolean>,
        errorMessage: string = 'Email đã được sử dụng'
    ): AsyncValidatorFn {
        return this.createAsyncValidator({
            checkFn,
            errorKey: 'emailTaken',
            errorMessage,
            debounceTime: 500,
            minLength: 5
        });
    }

    /**
     * Tạo validator kiểm tra số điện thoại đã tồn tại
     */
    createPhoneExistsValidator(
        checkFn: (phone: string) => Observable<boolean>,
        errorMessage: string = 'Số điện thoại đã được sử dụng'
    ): AsyncValidatorFn {
        return this.createAsyncValidator({
            checkFn,
            errorKey: 'phoneTaken',
            errorMessage,
            debounceTime: 500,
            minLength: 10
        });
    }

    /**
     * Tạo validator với custom logic
     */
    createCustomAsyncValidator(
        validateFn: (value: any) => Observable<ValidationErrors | null>,
        debounceTime: number = 300
    ): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            const value = control.value;

            if (!value || (typeof value === 'string' && value.trim() === '')) {
                return of(null);
            }

            return timer(debounceTime).pipe(
                switchMap(() => validateFn(value)),
                catchError(() => of({ asyncError: { message: 'Lỗi khi kiểm tra' } })),
                first()
            );
        };
    }

    /**
     * Lấy kết quả từ cache
     */
    private getCachedResult(errorKey: string, value: string): boolean | null {
        const keyCache = this.cache.get(errorKey);
        if (keyCache && keyCache.has(value)) {
            return keyCache.get(value) || false;
        }
        return null;
    }

    /**
     * Lưu kết quả vào cache
     */
    private setCachedResult(errorKey: string, value: string, result: boolean): void {
        if (!this.cache.has(errorKey)) {
            this.cache.set(errorKey, new Map());
        }
        this.cache.get(errorKey)!.set(value, result);
    }

    /**
     * Clear cache
     */
    clearCache(errorKey?: string): void {
        if (errorKey) {
            this.cache.delete(errorKey);
        } else {
            this.cache.clear();
        }
    }
}
